'use client'

import { useFormContext, useStoreState } from '@ariakit/react'
import { ComponentProps, useId } from 'react'

import { Select } from '@syconium/little-miss-figgy'
import { ErrorMessage } from '@syconium/little-miss-figgy/dist/components/Input/InputErrorMessage'

export type FormSelectProps = {
  name: string
  required?: ComponentProps<typeof Select>['required']
  label?: ComponentProps<typeof Select>['label']
  renderSelectValue: ComponentProps<typeof Select>['renderSelectValue']
  children?: React.ReactNode
  onChange?: ComponentProps<typeof Select>['onChange']
  disabled?: boolean
}

/**
 * FormSelect component that integrates Select from little-miss-figgy with Ariakit form state.
 * Automatically handles error display based on form validation.
 *
 * @example
 * const form = useFormStore({ defaultValues: { country: '' } })
 * <Form store={form}>
 *   <FormSelect
 *     name="country"
 *     label="Country"
 *     renderSelectValue={(value) => value || 'Select a country'}
 *   >
 *     <SelectOption value="us">United States</SelectOption>
 *     <SelectOption value="ca">Canada</SelectOption>
 *   </FormSelect>
 * </Form>
 */
export const FormSelect = ({
  name,
  label,
  children,
  renderSelectValue,
  required,
  onChange,
  disabled,
}: FormSelectProps) => {
  const form = useFormContext()
  const errorId = useId()

  // Get field value, error, and touched state from form store
  const value = useStoreState(form, () => form?.getValue(name) ?? '')
  const error = useStoreState(form, () => form?.getError(name))
  const touched = useStoreState(form, () => form?.getFieldTouched(name))

  const hasError = error !== undefined && error !== null && error.length > 0
  const displayError = touched && hasError

  const onChangeCallback = (newValue: string) => {
    form?.setFieldTouched(name, true)
    form?.setValue(name, newValue)
    onChange?.(newValue)
  }

  return (
    <>
      <Select
        label={label}
        required={required}
        errorId={errorId}
        isInvalid={!!displayError}
        value={value}
        onChange={onChangeCallback}
        renderSelectValue={renderSelectValue}
        name={name}
        disabled={disabled}
      >
        {children}
      </Select>
      {displayError && error ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
    </>
  )
}
